import { useInView, useReducedMotion } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';

const EASE_OUT = (t: number): number => 1 - Math.pow(1 - t, 3);
const COUNT_MS = 1400;

/** A numeric target parsed out of a stat value, keeping its prefix/suffix. */
interface Parsed {
  prefix: string;
  target: number;
  suffix: string;
}

/**
 * Decide whether a value should count up. Plain integers and "+"-suffixed
 * numbers animate; years (e.g. "2020") and non-numeric words ("Millions") stay
 * static — counting a year from zero reads badly.
 */
function parse(value: string): Parsed | null {
  const match = value.match(/^(\D*)(\d[\d,]*)(\D*)$/);
  if (!match) return null;
  const target = Number(match[2]!.replace(/,/g, ''));
  if (!Number.isFinite(target)) return null;
  // Skip 4-digit years — animate everything else with digits.
  if (target >= 1990 && target <= 2100 && match[1] === '' && match[3] === '') return null;
  return { prefix: match[1] ?? '', target, suffix: match[3] ?? '' };
}

/** Renders a stat value, counting up to it the first time it scrolls into view. */
export function StatCounter({ value }: { value: string }): JSX.Element {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const reduce = useReducedMotion();
  // Memoized so its identity is stable across renders — otherwise the count-up
  // effect below re-runs every render and the number flickers.
  const parsed = useMemo(() => parse(value), [value]);
  const [display, setDisplay] = useState(() =>
    parsed ? `${parsed.prefix}0${parsed.suffix}` : value,
  );

  useEffect(() => {
    if (!parsed || reduce) {
      setDisplay(value);
      return;
    }
    if (!inView) return;
    let raf = 0;
    const start = performance.now();
    const tick = (now: number): void => {
      const t = Math.min(1, (now - start) / COUNT_MS);
      const n = Math.round(parsed.target * EASE_OUT(t));
      setDisplay(`${parsed.prefix}${n.toLocaleString()}${parsed.suffix}`);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, parsed, reduce, value]);

  return (
    <span ref={ref} className="tabular-nums">
      {display}
    </span>
  );
}
